// src/pages/LandlordsPage.jsx
import React from 'react';

export default function LandlordsPage() {
  const heroImage =
    'https://michaelpoole.co.uk/assets/e4166bb/images/covers/Michael-Poole-Estate-Agents-Teesside-Middlesbrough-842256834.jpg';

  const features = [
    { icon: '📊', title: 'Maximum exposure', text: 'Premium marketing across portals + social. Professional photos & floorplans.' },
    { icon: '🧾', title: 'Thorough referencing', text: 'Credit checks, affordability, Right to Rent & previous landlord checks.' },
    { icon: '⏱️', title: 'Fast turnaround', text: 'Streamlined onboarding and digital e-signatures to reduce voids.' },
    { icon: '👤', title: 'Dedicated manager', text: 'Single point of contact with regular updates and statements.' },
    { icon: '⚖️', title: 'Legal compliance', text: 'ASTs, deposit protection, prescribed info, notices & renewals handled.' },
    { icon: '🛠️', title: 'Maintenance handled', text: 'Trusted local contractors, transparent quotes, 24/7 emergency line.' },
  ];

  const tiers = [
    {
      tier: 'Let Only',
      lead: 'We find & reference tenants, you manage thereafter.',
      price: 'From 75% of first month’s rent',
      cta: 'Get tenant-find',
      points: ['Marketing on major portals', 'Accompanied viewings', 'Tenant referencing & Right to Rent', 'AST drafting & e-sign'],
      color: '#1e3a8a',
    },
    {
      tier: 'Rent Collection',
      lead: 'We collect rent and chase arrears; you handle repairs.',
      price: '8% + VAT',
      cta: 'Start rent collection',
      points: ['All Let Only services', 'Monthly rent collection', 'Arrears management', 'Statements & receipts'],
      color: '#0ea5e9',
    },
    {
      tier: 'Fully Managed',
      lead: 'Hands-off management from move-in to renewal.',
      price: '12% + VAT',
      cta: 'Go fully managed',
      points: ['All Rent Collection services', 'Repairs & contractor management', 'Routine inspections', 'Notices, renewals & compliance'],
      color: '#b91c1c',
    },
  ];

  const process = [
    { n: 1, t: 'Valuation & advice', d: 'We assess rental demand and recommend quick wins to boost yield.' },
    { n: 2, t: 'Marketing & viewings', d: 'Professional photography, listings go live, accompanied viewings.' },
    { n: 3, t: 'Referencing', d: 'Comprehensive checks, affordability, Right to Rent and deposit setup.' },
    { n: 4, t: 'Move-in & compliance', d: 'AST signed, inventory, smoke/CO checks and key handover.' },
    { n: 5, t: 'Ongoing management', d: 'Inspections, repairs, rent collection and renewals.' },
  ];

  const comparison = [
    ['Marketing on portals', true, true, true],
    ['Accompanied viewings', true, true, true],
    ['Tenant referencing', true, true, true],
    ['AST drafting & e-sign', true, true, true],
    ['Deposit registration', true, true, true],
    ['Monthly rent collection', false, true, true],
    ['Arrears management', false, true, true],
    ['Repairs coordination', false, false, true],
    ['Routine inspections', false, false, true],
    ['Notices/renewals', false, false, true],
  ];

  return (
    <>
      <style>{`
        /* ========= Landlords Page (namespaced) ========= */
        .mrm-land { --brand:#1e3a8a; --brand2:#0ea5e9; --ink:#0f172a; --muted:#475569; --bg:#f8fafc; --paper:#fff; --line:#e5e7eb; color:var(--ink); font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial; }
        .mrm-land a { color: var(--brand2); text-decoration: none; }
        .mrm-land a:hover { text-decoration: underline; }
        .mrm-land__container { max-width:1200px; margin:0 auto; padding: 1rem; }

        /* HERO */
        .mrm-land__hero{
          position:relative; color:#fff; min-height:72vh; display:grid; align-items:center; isolation:isolate;
          background-image:
            linear-gradient(180deg, rgba(2,6,23,.35) 0%, rgba(2,6,23,.75) 100%),
            var(--hero);
          background-size:cover; background-position:center;
        }
        .mrm-land__crumb{ opacity:.85; margin-bottom:.5rem; }
        .mrm-land__title{ margin:.2rem 0 .6rem; font-weight:900; line-height:1.05; font-size: clamp(1.8rem, 4vw, 3rem); text-shadow:0 2px 18px rgba(0,0,0,.25); }
        .mrm-land__lede{ max-width: 860px; font-size: clamp(1rem, 1.9vw, 1.15rem); opacity:.95; }
        .mrm-land__cta{ display:flex; gap:.8rem; flex-wrap:wrap; margin-top:1.25rem; }
        .btn{ display:inline-flex; align-items:center; justify-content:center; font-weight:900; border-radius:999px; padding:.9rem 1.2rem; border:1px solid rgba(255,255,255,.7); color:#fff; background:transparent; cursor:pointer; transition:.2s transform,.25s box-shadow,.2s background; text-decoration:none; }
        .btn:hover{ transform: translateY(-1px); box-shadow:0 10px 22px rgba(0,0,0,.25); }
        .btn--solid{ background: var(--brand2); border-color: var(--brand2); }
        .btn--outline{ background: transparent; color:#fff; }
        .mrm-land__chips{ display:flex; gap:.5rem; flex-wrap:wrap; margin-top:1rem; }
        .chip{ background:rgba(255,255,255,.12); border:1px solid rgba(255,255,255,.3); color:#fff; padding:.4rem .6rem; border-radius:10px; font-weight:800; font-size:.84rem; }

        /* White card on hero (right) */
        .mrm-land__float{
          margin-top:1.5rem;
          background:#fff; color:var(--ink);
          border:1px solid var(--line); border-radius:16px; box-shadow:0 12px 28px rgba(2,6,23,.18);
          padding:1.1rem;
        }
        @media (min-width: 992px){
          .mrm-land__hero-grid{ display:grid; grid-template-columns: 7fr 5fr; gap:2rem; align-items:center; }
        }
        .mrm-land__float h3{ margin:.2rem 0; font-size:1.1rem; font-weight:900; color:var(--brand); }
        .mrm-land__float p{ margin:.2rem 0 1rem; color:#475569; }
        .row{ display:flex; gap:.8rem; flex-wrap:wrap; align-items:center; }
        .divider{ height:1px; background: var(--line); margin: 1rem 0; }
        .muted{ color:#64748b; font-size:.85rem; display:flex; gap:.4rem; align-items:center; }

        /* SECTIONS */
        .section{ padding: clamp(3rem, 6vw, 5.5rem) 0; background: var(--bg); }
        .section--alt{ background:#fff; }
        .h{ margin:0; font-weight:900; font-size: clamp(1.4rem, 3vw, 2rem); color: var(--brand); }
        .sub{ margin:.35rem 0 1rem; color: var(--muted); max-width: 900px; }

        /* Feature grid */
        .grid{ display:grid; grid-template-columns:1fr; gap:1rem; margin-top:1rem; }
        @media (min-width: 760px){ .grid--3{ grid-template-columns:1fr 1fr 1fr; } .grid--2{ grid-template-columns:1fr 1fr; } }
        @media (min-width: 1100px){ .grid--4{ grid-template-columns:1fr 1fr 1fr 1fr; } }
        .card{ background:#fff; border:1px solid var(--line); border-radius:14px; box-shadow:0 10px 22px rgba(2,6,23,.06); padding:1rem; height:100%; }
        .card__icon{ width:44px; height:44px; border-radius:12px; display:grid; place-items:center; background:#dbeafe; color:#0b3aa4; font-weight:900; font-size:1.15rem; margin-bottom:.4rem; }
        .card h3{ margin:.2rem 0 0; font-size:1rem; font-weight:900; }
        .card p{ margin:0; color:#475569; font-size:.95rem; line-height:1.55; }

        /* Pricing */
        .pricing{ display:grid; grid-template-columns:1fr; gap:1rem; margin-top:1rem; }
        @media (min-width: 992px){ .pricing{ grid-template-columns:1fr 1fr 1fr; } }
        .price-card{ background:#fff; border:1px solid var(--line); border-radius:16px; box-shadow:0 12px 28px rgba(2,6,23,.06); display:flex; flex-direction:column; }
        .price-card__body{ padding:1rem; }
        .price-card__tier{ font-size:.75rem; letter-spacing:.08em; text-transform:uppercase; color:#64748b; font-weight:900; }
        .price-card__lead{ margin:.2rem 0 .35rem; font-weight:900; }
        .price-card__price{ font-weight:900; margin-bottom:.8rem; }
        .ticks{ list-style:none; padding:0; margin:0; }
        .ticks li{ display:flex; gap:.5rem; align-items:flex-start; margin:.3rem 0; }
        .ticks li .t{ color:#10b981; font-weight:900; }
        .price-card__footer{ padding:1rem; }
        .btn--block{ width:100%; }

        /* Comparison table */
        .table-wrap{ overflow:auto; border:1px solid var(--line); border-radius:14px; }
        table{ width:100%; border-collapse:collapse; background:#fff; }
        thead th, tbody td{ padding:.7rem .8rem; border-bottom:1px solid var(--line); text-align:left; }
        thead th{ background:#f1f5f9; font-size:.92rem; }
        tbody td.center{ text-align:center; }
        .tick{ color:#10b981; font-weight:900; }

        /* Process */
        .step{ background:#fff; border:1px solid var(--line); border-radius:14px; padding:1rem; display:flex; gap:.85rem; height:100%; }
        .step__n{ flex:0 0 auto; width:36px; height:36px; border-radius:50%; background:var(--brand); color:#fff; font-weight:900; display:grid; place-items:center; }
        .step h4{ margin:.1rem 0 .1rem; font-size:1rem; font-weight:900; }
        .step p{ margin:0; color:#475569; font-size:.95rem; }

        /* Logos strip */
        .logo-grid{ display:grid; grid-template-columns:1fr 1fr; gap:.8rem; }
        @media (min-width: 760px){ .logo-grid{ grid-template-columns: repeat(4, 1fr); } }
        .logo-cell{ background:#f1f5f9; border:1px solid var(--line); border-radius:12px; height:72px; display:grid; place-items:center; }

        /* FAQ */
        .faq-grid{ display:grid; grid-template-columns:1fr; gap:.7rem; }
        @media (min-width: 900px){ .faq-grid{ grid-template-columns:1fr 1fr; } }
        details{ background:#fff; border:1px solid var(--line); border-radius:12px; padding:.7rem 1rem; }
        summary{ cursor:pointer; font-weight:900; list-style:none; }
        summary::-webkit-details-marker{ display:none; }

        /* FINAL CTA band */
        .band{
          color:#fff; border-radius:18px; overflow:hidden; position:relative; padding: clamp(1.2rem, 4vw, 2rem);
          background: linear-gradient(135deg, var(--brand), var(--brand2));
          display:grid; grid-template-columns:1fr; gap:1rem; align-items:center;
        }
        @media (min-width:900px){ .band{ grid-template-columns: 2fr 1fr; } }

        /* Back to top */
        .top{ position:fixed; right:18px; bottom:18px; border:none; background:var(--brand); color:#fff; width:44px; height:44px; border-radius:50%; cursor:pointer; box-shadow:0 10px 22px rgba(2,6,23,.25); font-weight:900; }
        .top:hover{ transform: translateY(-1px); }
      `}</style>

      <main className="mrm-land">
        {/* HERO */}
        <header className="mrm-land__hero" style={{ '--hero': `url('${heroImage}')` }}>
          <div className="mrm-land__container">
            <div className="mrm-land__crumb">
              <a href="/">Home</a> &rsaquo; Landlords
            </div>

            <div className="mrm-land__hero-grid">
              <div>
                <h1 className="mrm-land__title">
                  Let with confidence. <span style={{ color: '#93c5fd' }}>We manage, you relax.</span>
                </h1>
                <p className="mrm-land__lede">
                  One dedicated Middlesbrough office, local experts, deep neighbourhood knowledge.
                  From tenant-find to fully managed, we protect your asset and maximise your return.
                </p>

                <div className="mrm-land__cta">
                  <a href="/sellers/valuation" className="btn btn--outline">Free rental valuation</a>
                  <a href="/contact" className="btn btn--outline">Talk to an expert ☎</a>
                </div>

                <div className="mrm-land__chips" role="list">
                  <span className="chip" role="listitem">⚡ Average let in 10 days*</span>
                  <span className="chip" role="listitem">📈 4.74 / 5 — 1,571 reviews</span>
                  <span className="chip" role="listitem">🛡️ Client Money Protection</span>
                </div>
              </div>

              {/* Floating card */}
              <aside className="mrm-land__float" aria-label="Instant rent estimate">
                <h3>Instant rent estimate</h3>
                <p>See what your property could achieve in the current market. No obligation.</p>
                <div className="row">
                  <a href="/sellers/valuation" className="btn" style={{ borderColor:'#1e3a8a' }}>Get estimate</a>
                  <a href="/about#team" className="btn" style={{ color:'#1e3a8a', borderColor:'var(--line)', background:'#fff' }}>Meet the team</a>
                </div>
                <div className="divider"></div>
                <div className="muted">✅ Registered office: 142 Victoria Road, Middlesbrough, TS1 3HU • Incorporated 15 Apr 2019</div>
              </aside>
            </div>
          </div>
        </header>

        {/* WHY CHOOSE US */}
        <section className="section">
          <div className="mrm-land__container">
            <h2 className="h">Why landlords choose MRM</h2>
            <p className="sub">From rigorous tenant referencing to 24/7 maintenance, our proactive management keeps you compliant and your investment performing.</p>

            <div className="grid grid--3">
              {features.map((f) => (
                <article className="card" key={f.title}>
                  <div className="card__icon" aria-hidden="true">{f.icon}</div>
                  <h3>{f.title}</h3>
                  <p>{f.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* SERVICES & PRICING */}
        <section className="section section--alt">
          <div className="mrm-land__container">
            <h2 className="h">Services & pricing</h2>
            <p className="sub">Choose the level of service that suits you best. Fees subject to property type and location.</p>

            <div className="pricing">
              {tiers.map((p) => (
                <article className="price-card" key={p.tier}>
                  <div className="price-card__body">
                    <div className="price-card__tier">{p.tier}</div>
                    <h3 className="price-card__lead">{p.lead}</h3>
                    <div className="price-card__price" style={{ color: p.color }}>{p.price}</div>
                    <ul className="ticks">
                      {p.points.map((pt) => (
                        <li key={pt}><span className="t">✓</span><span>{pt}</span></li>
                      ))}
                    </ul>
                  </div>
                  <div className="price-card__footer">
                    <a href="/contact" className="btn btn--block" style={{  borderColor:p.color }}> {p.cta} </a>
                  </div>
                </article>
              ))}
            </div>

            {/* Comparison table */}
            <div className="table-wrap" style={{ marginTop:'1rem' }}>
              <table aria-label="Service comparison">
                <thead>
                  <tr>
                    <th>What’s included</th>
                    <th className="center">Let Only</th>
                    <th className="center">Rent Collection</th>
                    <th className="center">Fully Managed</th>
                  </tr>
                </thead>
                <tbody>
                  {comparison.map(([label, a, b, c]) => (
                    <tr key={label}>
                      <td>{label}</td>
                      {[a, b, c].map((v, i) => (
                        <td key={i} className="center">{v ? <span className="tick">✓</span> : '—'}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="row" style={{ marginTop:'1rem' }}>
              <span style={{ display:'inline-flex', alignItems:'center', gap:'.5rem', color:'#334155' }}>📄</span>
              <a href="/downloads/MRM-Landlord-Guide.pdf">Download our Landlord Guide (PDF)</a>
            </div>
          </div>
        </section>

        {/* PROCESS */}
        <section className="section">
          <div className="mrm-land__container">
            <h2 className="h">Our letting process</h2>
            <div className="grid grid--3">
              {process.map((s) => (
                <article className="step" key={s.n}>
                  <div className="step__n">{s.n}</div>
                  <div>
                    <h4>{s.t}</h4>
                    <p>{s.d}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* COMPLIANCE & MEMBERSHIPS */}
        <section className="section section--alt">
          <div className="mrm-land__container">
            <div className="grid grid--2" style={{ alignItems:'center' }}>
              <div>
                <h3 className="h">Professional standards & client protection</h3>
                <p className="sub">We adhere to industry best practice and maintain robust client money handling.</p>
                <ul className="ticks" style={{ marginTop:'.6rem' }}>
                  <li><span className="t">✓</span>Client Money Protection scheme</li>
                  <li><span className="t">✓</span>Deposit protection & prescribed information</li>
                  <li><span className="t">✓</span>ICO registration & GDPR compliant</li>
                </ul>
                <div className="row" style={{ marginTop:'0.8rem' }}>
                  <a href="/privacy" className="btn" style={{ color:'var(--brand)', background:'#fff', borderColor:'var(--line)' }}>Privacy & GDPR</a>
                  <a href="/terms" className="btn" style={{ color:'var(--brand)', background:'#fff', borderColor:'var(--line)' }}>Terms of Business</a>
                </div>
              </div>
              <div className="logo-grid" aria-label="Accreditations">
                {['ARLA','CMP','TPO','ICO'].map((k) => (
                  <div className="logo-cell" key={k}>{k}</div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="section">
          <div className="mrm-land__container">
            <h2 className="h">Landlord FAQs</h2>
            <div className="faq-grid" style={{ marginTop:'1rem' }}>
              {[
                ['How quickly can you let my property?', 'Speed depends on location, price and condition. Our average is around 10 days from marketing to let agreed in active markets.'],
                ['What documents do you handle?', 'Assured Shorthold Tenancy (AST), deposit registration, prescribed information, Right to Rent, smoke/CO checks and inventory.'],
                ['Do you manage maintenance?', 'Yes. We use vetted local contractors and always seek approval for larger works. Emergency line available 24/7.'],
                ['Can you guarantee rent?', 'We can arrange rent protection products on request. Ask our team for details and eligibility.'],
                ['What are your fees?', 'See the services section above for headline fees. Full fee schedule available in our Terms of Business.'],
              ].map(([q, a]) => (
                <details key={q}>
                  <summary>{q}</summary>
                  <div style={{ marginTop: '.4rem', color: '#475569' }}>{a}</div>
                </details>
              ))}
            </div>
          </div>
        </section>

       
      </main>

      {/* Back to top */}
      <button
        className="top"
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        aria-label="Back to top"
        title="Back to top"
      >
        ↑
      </button>
    </>
  );
}
